import { useTranslation } from 'react-i18next';

import { useAppearance } from '@/hooks/useAppearance';
import { PRESETS } from '@/lib/appearance';
import { cn } from '@/lib/cn';

/**
 * Appearance section for the Settings screen: pick a theme preset or set a
 * custom background + accent. Live-applies via `useAppearance` (CSS vars +
 * Telegram chrome) and persists to localStorage. Phase 1 — backend-synced /
 * unlockable / AI-generated themes come in Phase 3.
 */
export function AppearanceSettings() {
  const { t } = useTranslation('general');
  const { prefs, presetId, setPreset, setCustomColors, reset } = useAppearance();

  const customBg = prefs.customBg ?? '#000000';
  const customAccent = prefs.customAccent ?? '#00e7df';

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
        {t('appearance', { defaultValue: 'Appearance' })}
      </h2>

      {/* Preset swatches */}
      <div className="grid grid-cols-2 gap-2">
        {PRESETS.map((preset) => {
          const active = presetId === preset.id;
          const pal = preset.palette;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => setPreset(preset.id)}
              className={cn(
                'flex items-center gap-3 rounded-xl border p-3 text-left transition-opacity active:opacity-80',
                active ? 'border-brand' : 'border-border',
              )}
            >
              <span
                aria-hidden
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: pal['--color-bg'],
                  boxShadow: `inset 0 0 0 1px ${pal['--color-border']}`,
                }}
              >
                <span
                  className="h-4 w-4 rounded-full"
                  style={{ backgroundColor: pal['--color-brand'] }}
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-fg">{preset.name}</span>
                {active && <span className="text-xs font-medium text-brand">Active</span>}
              </span>
            </button>
          );
        })}
      </div>

      {/* Custom colours */}
      <div className="flex flex-col rounded-xl bg-bg-secondary">
        <span className="px-3 pt-3 text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('customColors', { defaultValue: 'Custom colours' })}
        </span>
        <label className="flex items-center justify-between px-3 py-3">
          <span className="text-sm text-fg">
            {t('background', { defaultValue: 'Background' })}
          </span>
          <input
            type="color"
            aria-label={t('background', { defaultValue: 'Background' })}
            value={customBg}
            onChange={(e) => setCustomColors({ bg: e.target.value })}
            className="h-8 w-12 cursor-pointer rounded border border-border bg-transparent"
          />
        </label>
        <label className="flex items-center justify-between px-3 py-3">
          <span className="text-sm text-fg">{t('accent', { defaultValue: 'Accent' })}</span>
          <input
            type="color"
            aria-label={t('accent', { defaultValue: 'Accent' })}
            value={customAccent}
            onChange={(e) => setCustomColors({ accent: e.target.value })}
            className="h-8 w-12 cursor-pointer rounded border border-border bg-transparent"
          />
        </label>
      </div>

      <button
        type="button"
        onClick={reset}
        className="self-start text-xs font-medium text-fg-muted transition-opacity active:opacity-80"
      >
        {t('resetTheme', { defaultValue: 'Reset to default' })}
      </button>
    </section>
  );
}
